export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  authorAvatar: string;
  date: string;
  readTime: number;
  imageUrl: string;
  thumbnailUrl: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: 'art-of-cat-tea-ceremony',
    title: '猫与茶道：东方美学中的猫咪哲学',
    excerpt: '在袅袅茶香中，猫咪以一种超然物外的姿态，完美诠释了东方美学中的"侘寂"与"幽玄"。本文将带您走进猫与茶道的优雅世界。',
    content: `<p>在日本茶道文化中，有一句古老的谚语："猫の手も借りたい"（忙得连猫的手都想借）。这句谚语虽然形容极度忙碌，却也暗示了猫在日本文化中的特殊地位。</p>
    <p>茶道追求的是"和敬清寂"的精神境界，而猫的存在，恰如其分地融入了这一哲学体系。当我们观察一只猫在茶室中悠然漫步，或是静静地蜷缩在榻榻米一角，那种不被打扰的宁静，正是茶道所追求的最高境界。</p>
    <h2>侘寂之美</h2>
    <p>侘寂（Wabi-sabi）是日本美学的核心概念，强调不完美、不恒常、不完整的美。猫的优雅中带着一丝慵懒，端庄中透出几分随性——这种矛盾的和谐，正是侘寂之美的绝佳体现。</p>
    <p>在茶室中，一只猫咪随意卧于角落的姿态，胜过千言万语的装饰。它不需要刻意表现什么，仅仅存在本身，就已经构成了一幅完美的画面。</p>
    <h2>猫与禅意</h2>
    <p>禅宗讲究"直指人心，见性成佛"，而猫的生活方式，恰恰暗合了这一哲学。它不被过去所困，不为未来焦虑，活在每一个当下——打盹时全情投入，玩耍时尽兴而为。</p>
    <p>这种"活在当下"的生活态度，正是现代绅士们最需要学习的功课。在这个信息爆炸的时代，能在纷扰中保持内心的宁静，如同茶室中的猫一般，是一种珍贵的品格。</p>
    <h2>茶室中的猫</h2>
    <p>许多日本茶道大师都曾养猫。千利休据说就有一只名叫"宗旦"的爱猫。茶人们认为，猫的存在能够净化空间的气息，让茶室的氛围更加和谐自然。</p>
    <p>在现代生活中，我们同样可以在品茶时邀请爱猫相伴。一杯好茶，一只猫咪，一本好书——这便是喵绅士最基本的日常仪式。</p>`,
    category: 'cat-culture',
    author: '森下和也',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Japanese%20gentleman%20portrait%20in%20traditional%20kimono%20with%20cat%2C%20warm%20lighting%2C%20elegant%20studio%20photography%2C%20soft%20background&width=80&height=80&seq=author-kazuya&orientation=squarish',
    date: '2025-12-15',
    readTime: 8,
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cat%20in%20Japanese%20tea%20room%20with%20tatami%20and%20tea%20set%2C%20warm%20ambient%20light%2C%20wabi-sabi%20interior%2C%20soft%20shadows%2C%20zen%20atmosphere&width=800&height=500&seq=tea-ceremony-cat&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cat%20in%20Japanese%20tea%20room%20with%20tatami%2C%20warm%20light%2C%20zen%2C%20minimalist%20style&width=160&height=160&seq=tea-ceremony-cat-thumb&orientation=squarish',
    tags: ['茶道', '日本文化', '侘寂', '猫咪哲学'],
    featured: true,
  },
  {
    slug: 'gentleman-cat-wardrobe',
    title: '绅士猫的衣橱：如何为猫咪打造高品味生活空间',
    excerpt: '一位真正的喵绅士，不仅自身品味出众，更要为爱猫打造一个兼具美感与舒适的生活空间。从猫爬架到食盆，每一个细节都不容忽视。',
    content: `<p>在传统的养猫观念中，猫咪用品似乎只需要满足"实用"这一个条件即可。但对于追求生活品质的喵绅士而言，猫的居住空间同样需要精心设计。</p>
    <p>一个高品味的猫咪生活空间，应该像一件艺术品一样，既服务于猫咪的天性需求，又能和谐地融入主人的家居美学。</p>
    <h2>猫爬架：从功能到艺术</h2>
    <p>传统的猫爬架往往色彩艳丽、造型夸张，与现代家居风格格格不入。而新一代的设计师猫爬架，采用了天然木材、棉麻绳索等材质，以简约流畅的线条重新诠释了这一猫咪必备家具。</p>
    <p>选择猫爬架时，应遵循"少即是多"的原则。一件造型优雅、材质上乘的猫爬架，胜过多件廉价而花哨的产品。</p>
    <h2>猫咪寝具的讲究</h2>
    <p>猫咪每天约有16小时在睡眠中度过，一张舒适的猫床至关重要。建议选择天然材质——羊毛毡、有机棉、亚麻——这些材料不仅触感舒适，还能自然调节温度。</p>
    <h2>食器的美学</h2>
    <p>猫咪的食盆和水盆，不必局限于宠物用品的范畴。许多高品质的陶瓷碗、玻璃器皿，同样适合作为猫咪的餐具。选择与家居风格协调的款式，让猫咪的用餐区域也成为家中的一道风景。</p>
    <h2>空间规划的艺术</h2>
    <p>猫咪是立体空间的动物，它们需要垂直的活动空间。在规划猫咪生活区时，可以利用墙面层板、窗台延伸等设计，为猫咪创造富有层次感的活动路线。</p>`,
    category: 'gentleman-aesthetics',
    author: '王一鸣',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20gentleman%20with%20glasses%20in%20modern%20studio%2C%20warm%20tone%20portrait%2C%20elegant%20composition&width=80&height=80&seq=author-wang&orientation=squarish',
    date: '2025-12-10',
    readTime: 6,
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20cat%20furniture%20in%20modern%20interior%2C%20wooden%20cat%20tree%2C%20ceramic%20bowls%2C%20warm%20natural%20light%2C%20scandinavian%20design&width=800&height=500&seq=cat-wardrobe&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20cat%20furniture%20in%20modern%20interior%2C%20warm%20light%2C%20scandinavian%20design&width=160&height=160&seq=cat-wardrobe-thumb&orientation=squarish',
    tags: ['家居设计', '猫咪用品', '生活美学', '空间规划'],
    featured: true,
  },
  {
    slug: 'cats-in-literature',
    title: '文学中的猫：从夏目漱石到村上春树',
    excerpt: '猫一直是文学作品中不可或缺的角色。从夏目漱石的《我是猫》到村上春树笔下那些神秘的猫，它们承载了作家们对人性最深沉的思考。',
    content: `<p>在文学世界中，猫占据了独特的地位。它们以观察者的身份，旁观人类世界的荒诞与温情。从东方的夏目漱石到西方的爱伦·坡，猫始终是作家们钟爱的主题。</p>
    <h2>《我是猫》：一只猫眼中的明治日本</h2>
    <p>夏目漱石的《我是猫》，以一只无名猫的视角，辛辣地讽刺了明治时代知识分子的虚伪与软弱。这只猫没有名字，却比任何有名有姓的角色都更加生动鲜活。</p>
    <p>"吾辈是猫，名字嘛，还没有。"——这个经典的开篇，奠定了整部作品的基调：旁观、嘲讽、却又不失温情。</p>
    <h2>村上春树的猫</h2>
    <p>在村上春树的小说中，猫是连接现实与超现实的媒介。《海边的卡夫卡》中能与猫对话的中田老人，《1Q84》中神秘的空气蛹与两只月亮，猫始终游走在两个世界之间。</p>
    <h2>西方文学中的猫</h2>
    <p>爱伦·坡的《黑猫》以惊悚的笔触描绘了人与猫之间的复杂关系；海明威在基韦斯特的家中养了数十只六趾猫，它们成为了他写作生涯的忠实伴侣；T.S.艾略特的《老负鼠的实用猫经》更是被改编为音乐剧《猫》，风靡全球。</p>`,
    category: 'cat-culture',
    author: '林语晴',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Asian%20woman%20reader%20portrait%20in%20library%2C%20warm%20tone%2C%20elegant%20intellectual%20style&width=80&height=80&seq=author-lin&orientation=squarish',
    date: '2025-12-08',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Cat%20sitting%20on%20vintage%20books%20in%20library%2C%20warm%20reading%20lamp%20glow%2C%20antique%20bookshelf%2C%20literary%20atmosphere%2C%20soft%20dust%20motes&width=800&height=500&seq=cats-literature&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Cat%20on%20vintage%20books%20in%20library%2C%20warm%20light%2C%20literary%20atmosphere&width=160&height=160&seq=cats-literature-thumb&orientation=squarish',
    tags: ['文学', '夏目漱石', '村上春树', '猫咪文化'],
    featured: true,
  },
  {
    slug: 'cat-nutrition-guide',
    title: '喵绅士营养学：科学喂养的艺术',
    excerpt: '喂猫不只是倒猫粮那么简单。一份真正讲究的猫咪食谱，应当兼顾营养均衡、食材品质与进餐仪式感。',
    content: `<p>在喵绅士的养猫哲学中，喂食不仅是满足猫咪的生理需求，更是一门值得深入研究的学问。科学的营养搭配、优质的食材选择、优雅的进餐环境——三者缺一不可。</p>
    <h2>猫咪的营养需求基础</h2>
    <p>作为纯肉食动物，猫咪对蛋白质的需求远高于杂食动物。优质的动物蛋白应当占据猫咪日常摄入的绝大部分。此外，牛磺酸、精氨酸等特定氨基酸对猫咪的健康至关重要。</p>
    <h2>干粮与湿粮的平衡</h2>
    <p>干粮便利，湿粮补水，两者各有优势。喵绅士推荐以高品质湿粮为主（占比60-70%），辅以优质干粮的喂养方案。这样的搭配既能保证水分摄入，又不失便利性。</p>
    <h2>自制猫饭的讲究</h2>
    <p>越来越多讲究的铲屎官开始尝试自制猫饭。但自制猫饭需要严格遵循营养学原则：精确的肉、内脏、骨骼比例，必要的营养补充剂添加，以及食材新鲜度的把控。</p>
    <h2>进餐仪式感</h2>
    <p>在喵绅士看来，猫咪的进餐也应当有仪式感。固定时间、固定地点、安静的进餐环境，这些细节能让猫咪更加从容地享用美食，也有助于消化健康。</p>`,
    category: 'cat-care-guide',
    author: '陈医生',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Veterinarian%20portrait%20in%20white%20coat%20with%20cat%2C%20professional%20studio%20photography&width=80&height=80&seq=author-chen&orientation=squarish',
    date: '2025-12-05',
    readTime: 7,
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cat%20eating%20from%20ceramic%20bowl%20on%20wooden%20table%2C%20natural%20light%2C%20minimalist%20kitchen%2C%20fresh%20ingredients%2C%20warm%20atmosphere&width=800&height=500&seq=cat-nutrition&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cat%20eating%20from%20ceramic%20bowl%2C%20minimalist%20kitchen%2C%20warm%20light&width=160&height=160&seq=cat-nutrition-thumb&orientation=squarish',
    tags: ['科学喂养', '猫咪营养', '猫粮', '健康'],
    featured: false,
  },
  {
    slug: 'travel-with-cat',
    title: '与猫同行：绅士的猫咪旅行指南',
    excerpt: '谁说旅行不能带上猫咪？只要准备充分，猫咪也可以是完美的旅伴。从旅行箱到目的地选择，喵绅士为您提供全套优雅出行方案。',
    content: `<p>随着宠物友好文化的兴起，越来越多的酒店、餐厅和景点开始欢迎猫咪的到来。一场精心策划的猫咪旅行，可以为铲屎官和猫咪创造独一无二的美好回忆。</p>
    <h2>出发前的准备</h2>
    <p>猫咪旅行的关键在于充分的准备。一款安全舒适的航空箱是必备之物，此外还需要准备便携式猫砂盆、熟悉的玩具和毯子（帮助猫咪缓解陌生环境的焦虑），以及足够的食物和水。</p>
    <h2>选择猫咪友好的目的地</h2>
    <p>日本的猫岛、台湾的猫村、伊斯坦布尔的街头猫文化——世界上有许多地方以猫闻名。选择一个猫咪友好的目的地，能让旅行体验更加顺畅愉快。</p>
    <h2>旅途中的注意事项</h2>
    <p>长途旅行中，每隔2-3小时应当停车休息，让猫咪有机会喝水和使用猫砂盆。到达目的地后，先让猫咪在酒店房间内适应几个小时，再考虑外出探索。</p>`,
    category: 'elegant-living',
    author: '李游',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20traveler%20portrait%20with%20cat%2C%20outdoor%20warm%20lighting&width=80&height=80&seq=author-li&orientation=squarish',
    date: '2025-11-28',
    readTime: 6,
    imageUrl: 'https://readdy.ai/api/search-image?query=Cat%20in%20elegant%20carrier%20looking%20out%20window%20at%20scenic%20landscape%2C%20travel%20aesthetic%2C%20warm%20golden%20light%2C%20adventure%20vibe&width=800&height=500&seq=travel-cat&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Cat%20in%20elegant%20carrier%20at%20window%2C%20scenic%20view%2C%20warm%20light&width=160&height=160&seq=travel-cat-thumb&orientation=squarish',
    tags: ['旅行', '猫咪出行', '宠物友好', '生活方式'],
    featured: false,
  },
  {
    slug: 'cat-whiskers-mystery',
    title: '猫须之谜：解读猫咪最优雅的感官器官',
    excerpt: '猫咪的胡须不仅是可爱的装饰，更是精密的感官系统。每一根胡须都连接着丰富的神经网络，是猫咪感知世界的优雅工具。',
    content: `<p>猫须（触须）是猫咪身上最迷人的特征之一。这些看似简单的毛发，实际上是高度进化的触觉器官，在猫咪的日常生活中扮演着不可替代的角色。</p>
    <h2>猫须的解剖学</h2>
    <p>猫须的根部比普通毛发深三倍以上，深深嵌入皮肤中，周围密布着神经末梢和血管。这使得每一根猫须都能感受到最微小的空气流动变化。</p>
    <h2>猫须的功能</h2>
    <p>猫须是猫咪的"测量尺"——猫咪通过胡须判断自己能否穿过狭窄的缝隙。此外，猫须还能感知气流变化，帮助猫咪在黑暗中导航，甚至能"读取"猎物的微弱震动。</p>
    <h2>猫须与情绪</h2>
    <p>猫须的位置也是解读猫咪情绪的窗口：放松时猫须自然向两侧伸展；警觉时猫须会向前竖起；害怕时猫须则会紧贴面部。学会阅读猫须语言，是成为喵绅士的必修课。</p>
    <h2>猫须的护理</h2>
    <p>猫须会自然脱落和再生，这是正常的生理现象。切记不要修剪或拉扯猫须——这会让猫咪失去重要的感官信息，造成极大的不适和焦虑。</p>`,
    category: 'cat-care-guide',
    author: '陈医生',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Veterinarian%20portrait%20in%20white%20coat%20with%20cat%2C%20professional%20studio%20photography&width=80&height=80&seq=author-chen&orientation=squarish',
    date: '2025-11-20',
    readTime: 5,
    imageUrl: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20elegant%20cat%20face%20with%20beautiful%20whiskers%2C%20macro%20photography%2C%20soft%20natural%20light%2C%20cream%20background%2C%20detailed%20fur%20texture&width=800&height=500&seq=cat-whiskers&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Close%20up%20elegant%20cat%20face%20whiskers%2C%20macro%2C%20soft%20light&width=160&height=160&seq=cat-whiskers-thumb&orientation=squarish',
    tags: ['猫咪生理', '猫须', '宠物科普', '健康护理'],
    featured: false,
  },
  {
    slug: 'minimalist-cat-home',
    title: '极简主义猫宅：当北欧设计遇见喵星人',
    excerpt: '极简风格与养猫并非水火不容。通过巧妙的设计，您可以在保持空间简洁优雅的同时，满足猫咪的一切需求。',
    content: `<p>许多崇尚极简主义的人士担心养猫会破坏空间的整洁与秩序。然而，通过精心的设计和规划，猫咪完全可以和谐地融入极简风格的家居环境中。</p>
    <h2>隐藏式猫设施的智慧</h2>
    <p>最好的猫设施，是让人察觉不到存在的猫设施。将猫砂盆巧妙嵌入定制柜体中，把猫爬架设计成墙面装饰的一部分，让猫抓柱化身为一件艺术品——这些巧思让猫咪功能与空间美学完美共存。</p>
    <h2>色彩统一的力量</h2>
    <p>选择与家居色调一致的猫咪用品，是维持视觉统一的关键。如果您的家以自然木色和白色为主调，那么猫咪的食盆、猫床和玩具也应遵循相同的色彩方案。</p>
    <h2>材质的选择</h2>
    <p>极简风格偏好天然材质——木材、亚麻、棉麻、石材。优先选择这些材质的猫咪用品，它们不仅能融入空间，还能为猫咪提供更加舒适的触感体验。</p>
    <h2>收纳的艺术</h2>
    <p>猫咪的玩具、零食、护理用品需要有序收纳。一组设计简洁的收纳篮或抽屉，既能保持空间整洁，又方便日常取用。记住：看得见的东西越少，空间越显优雅。</p>`,
    category: 'gentleman-aesthetics',
    author: '王一鸣',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20gentleman%20with%20glasses%20in%20modern%20studio%2C%20warm%20tone%20portrait%2C%20elegant%20composition&width=80&height=80&seq=author-wang&orientation=squarish',
    date: '2025-11-15',
    readTime: 7,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20scandinavian%20living%20room%20with%20cat%20on%20sofa%2C%20clean%20lines%2C%20natural%20wood%2C%20white%20walls%2C%20warm%20afternoon%20light%2C%20cozy&width=800&height=500&seq=minimalist-cat&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20living%20room%20cat%20on%20sofa%2C%20clean%20aesthetic%2C%20warm%20light&width=160&height=160&seq=minimalist-cat-thumb&orientation=squarish',
    tags: ['极简主义', '家居设计', '北欧风格', '猫宅'],
    featured: false,
  },
  {
    slug: 'rescue-cat-story',
    title: '领养日记：一只流浪猫的绅士蜕变之路',
    excerpt: '从瑟缩在城市角落的流浪猫，到优雅从容的家庭成员——这是一个关于爱与耐心的真实故事，也是喵绅士精神的完美诠释。',
    content: `<p>那是去年深秋的一个雨天，我在回家的路上遇见了它——一只浑身湿透、瑟缩在垃圾桶旁的小橘猫。它的眼神中既有恐惧，又有一丝无法掩饰的好奇。</p>
    <h2>初遇</h2>
    <p>我蹲下身，保持一段距离，轻声地和它说话。流浪猫对人类通常充满警惕，这是它们在大街上生存下来的本能。但它没有逃走——它只是静静地看着我，那双琥珀色的眼睛里仿佛在诉说着什么。</p>
    <h2>信任的建立</h2>
    <p>带它回家只是第一步，真正困难的是建立信任。它花了整整三周才愿意让我抚摸它，又过了一个月才敢跳上沙发。每一天的进步都很微小，但都值得铭记。</p>
    <h2>绅士的蜕变</h2>
    <p>如今，这只曾经瑟缩的小流浪猫已经蜕变成了一位真正的"猫绅士"。它会端庄地坐在餐桌旁等开饭，会在午后阳光最好的时候优雅地占据飘窗的最佳位置。它不再是那个恐惧人类的小生命，而是一个自信、从容、有尊严的家庭成员。</p>
    <h2>领养的意义</h2>
    <p>每一只流浪猫都值得被温柔以待。领养不仅仅是给它们一个家，更是给予它们重获尊严的机会。作为一名喵绅士，我深信：真正的优雅，在于善待每一个生命。</p>`,
    category: 'cat-stories',
    author: '张小雅',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20with%20orange%20cat%2C%20cozy%20home%20portrait%2C%20warm%20natural%20light&width=80&height=80&seq=author-zhang&orientation=squarish',
    date: '2025-11-10',
    readTime: 8,
    imageUrl: 'https://readdy.ai/api/search-image?query=Orange%20tabby%20cat%20sitting%20elegantly%20by%20window%20in%20cozy%20home%2C%20warm%20sunlight%2C%20transformation%20story%2C%20dignified%20pose%2C%20comfortable&width=800&height=500&seq=rescue-cat&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Orange%20tabby%20cat%20by%20window%20warm%20light%2C%20elegant%2C%20cozy&width=160&height=160&seq=rescue-cat-thumb&orientation=squarish',
    tags: ['猫咪领养', '流浪猫', '暖心故事', '喵绅士精神'],
    featured: true,
  },
  {
    slug: 'cat-art-collection',
    title: '猫咪艺术收藏指南：从浮世绘到当代插画',
    excerpt: '猫咪一直是艺术家们钟爱的题材。从江户时代的浮世绘到当代数字插画，猫咪艺术跨越国界与时代，始终焕发着迷人的魅力。',
    content: `<p>在艺术史上，猫的身影从未缺席。古埃及人将猫奉为神明，江户时代的浮世绘画师们以猫入画，毕加索、达利等现代艺术大师也都曾以猫为题材进行创作。</p>
    <h2>浮世绘中的猫</h2>
    <p>歌川国芳是江户时代最著名的"猫派"画师。他笔下的猫神态各异、栩栩如生，甚至将猫拟人化，让它们穿上和服、模仿人类的行为。这些作品不仅技艺精湛，更充满了幽默与温情。</p>
    <h2>西方艺术中的猫</h2>
    <p>从文艺复兴时期的宗教画中象征神秘的猫，到印象派画家笔下慵懒的家猫，西方艺术对猫的刻画经历了从象征符号到生活伴侣的转变。</p>
    <h2>当代猫咪艺术</h2>
    <p>在当代艺术领域，猫咪主题的作品依然层出不穷。数字艺术家们以全新的媒介诠释猫咪的魅力，猫咪摄影也成为了一个独立的艺术门类。</p>
    <h2>开始您的收藏</h2>
    <p>收藏猫咪艺术品不必从昂贵的原作开始。限量版画、独立插画师的作品、甚至是精美的猫咪摄影集，都是入门的好选择。关键是选择那些真正打动您的作品。</p>`,
    category: 'cat-culture',
    author: '林语晴',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Asian%20woman%20reader%20portrait%20in%20library%2C%20warm%20tone%2C%20elegant%20intellectual%20style&width=80&height=80&seq=author-lin&orientation=squarish',
    date: '2025-11-05',
    readTime: 9,
    imageUrl: 'https://readdy.ai/api/search-image?query=Japanese%20ukiyo-e%20style%20cat%20illustration%20with%20calligraphy%20brush%2C%20traditional%20art%20aesthetic%2C%20warm%20paper%20texture%2C%20elegant%20composition&width=800&height=500&seq=cat-art&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Japanese%20ukiyo-e%20cat%20illustration%2C%20traditional%20art%2C%20warm%20tone&width=160&height=160&seq=cat-art-thumb&orientation=squarish',
    tags: ['艺术收藏', '浮世绘', '插画', '猫咪艺术'],
    featured: false,
  },
  {
    slug: 'cat-massage-guide',
    title: '猫咪按摩术：增进感情的优雅手法',
    excerpt: '正确的按摩不仅能让猫咪放松身心，更是增进人猫感情的绝佳方式。学习喵绅士专属的猫咪按摩技法，让您的爱猫享受五星级SPA体验。',
    content: `<p>猫咪是天生的享乐主义者，它们懂得如何放松，也懂得如何享受。一套专业的猫咪按摩，不仅能让猫咪身心舒畅，还能帮助铲屎官及早发现猫咪身体上的异常。</p>
    <h2>按摩前的准备</h2>
    <p>选择猫咪放松的时刻——通常是饭后或者午睡醒来时。确保环境安静温暖，双手清洁温暖。可以先轻轻抚摸猫咪，观察它的反应，确保它愿意接受进一步的接触。</p>
    <h2>基础按摩技法</h2>
    <p>从猫咪最喜欢的部位开始：下巴和耳朵后方。用指腹以轻柔的圆周运动按摩，力度以猫咪感到舒适为准。逐渐扩展到脸颊、颈部和肩部。注意观察猫咪的反应——如果它发出呼噜声或主动蹭您的手，说明它很享受。</p>
    <h2>进阶按摩区域</h2>
    <p>当猫咪完全信任您之后，可以尝试按摩背部和尾部根部。背部的按摩应当沿着脊椎两侧进行，由前向后。尾根是很多猫咪的敏感愉悦区，轻柔按摩此处能让猫咪异常放松。</p>
    <h2>注意事项</h2>
    <p>腹部和爪子是大多数猫咪的禁区，除非您与猫咪建立了极高的信任度。按摩时间控制在10-15分钟为宜，过长的按摩反而会让猫咪感到烦躁。</p>`,
    category: 'cat-care-guide',
    author: '陈医生',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Veterinarian%20portrait%20in%20white%20coat%20with%20cat%2C%20professional%20studio%20photography&width=80&height=80&seq=author-chen&orientation=squarish',
    date: '2025-10-28',
    readTime: 5,
    imageUrl: 'https://readdy.ai/api/search-image?query=Person%20gently%20massaging%20relaxed%20cat%20on%20soft%20blanket%2C%20warm%20afternoon%20light%2C%20cozy%20interior%2C%20spa-like%20serene%20atmosphere&width=800&height=500&seq=cat-massage&orientation=landscape',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Person%20massaging%20relaxed%20cat%2C%20warm%20light%2C%20cozy&width=160&height=160&seq=cat-massage-thumb&orientation=squarish',
    tags: ['猫咪按摩', '健康护理', '人猫互动', '放松'],
    featured: false,
  },
];